import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SIDE_NAV_ITEMS, isSideNavItemActive } from '../lib/sideNav';
import styles from './SideNavDrawer.module.css';

export interface SideNavDrawerProps {
  open: boolean;
  onClose: () => void;
  /** useAuth().signOut — the SAME sign-out Settings.tsx's ProfileSection
   *  wires (POST /auth/logout, then a hard navigate to /goodbye). Passed in
   *  rather than importing useAuth directly so this component stays a pure
   *  presentation piece over props, matching AppShell's own "children in,
   *  nothing fetched here" shape. */
  onSignOut: () => Promise<void>;
}

/**
 * LANE W-NAV — the upper-left nav trigger's drawer (Jason, from a live
 * screenshot's pink top-left annotation: "I need upper left navigation on
 * every page it can trigger the side nav bar in from the left but we need
 * something besides the bottom only nav"). Slides in FROM THE LEFT over a
 * scrim, ADDITIVE to the bottom TabBar — TabBar.tsx is untouched, this is a
 * second route to the same destinations plus Settings + Sign out.
 *
 * Rendered from AppShell (see that file's own doc comment) so it is present
 * on EVERY routed page regardless of whether that page has its own header
 * (Refine's "Talk it through with Meira" kicker, Home's avatar+gear row,
 * Settings'/JobDetail's back-button) — the drawer is a shell-level overlay,
 * not something any individual page composes.
 *
 * Contents mirror TabBar's own 5 routes (sideNav.ts's SIDE_NAV_ITEMS, kept in
 * the same order) PLUS Settings (which the bottom bar has no slot for) and a
 * Sign out row at the bottom, reusing the exact useAuth().signOut Settings.tsx
 * already wires (same POST /auth/logout, same hard-navigate to /goodbye — see
 * useAuth.ts's own doc comment for why that is a hard, not soft, navigation).
 *
 * Close triggers, per the brief: a nav-row tap (onClick below, before the
 * navigation completes — Link's default behavior is unaffected), tapping the
 * overlay/scrim (.scrim's onClick), the X button, and Esc (the keydown
 * listener below). Focus is trapped in a minimal, dependency-free way: on
 * open, focus moves to the drawer's close button; Tab/Shift+Tab wrap within
 * the drawer's own focusable set (see trapFocus below) rather than escaping
 * to the (hidden-behind-the-scrim) page underneath.
 */
export function SideNavDrawer({ open, onClose, onSignOut }: SideNavDrawerProps) {
  const location = useLocation();
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  // Esc closes, from anywhere — matches the scrim/X/nav-tap close paths.
  // Only listens while open (no stray global listener sitting around while
  // closed, mirroring Orb.tsx's/useOnboardingGate's own "only subscribe when
  // it matters" discipline).
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab') trapFocus(e, drawerRef.current);
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Move focus INTO the drawer the moment it opens (WCAG dialog pattern) —
  // the close (X) button is the first focusable, sensible landing spot.
  useEffect(() => {
    if (open) closeButtonRef.current?.focus();
  }, [open]);

  async function handleSignOut() {
    onClose();
    await onSignOut();
  }

  return (
    <>
      {/* The scrim — a translucent ink overlay, tap-to-close. Rendered
       *  (rather than unmounted) at all times; CSS drives opacity/
       *  pointer-events off data-open so the slide-out transition can play
       *  instead of an abrupt unmount. aria-hidden because it carries no
       *  content of its own — it is never a tab stop either way. */}
      <div
        className={styles.scrim}
        data-open={open}
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        ref={drawerRef}
        className={styles.drawer}
        data-open={open}
        role="dialog"
        aria-modal={open}
        aria-label="Navigation"
        // A closed-but-still-in-DOM drawer must not be announced as present
        // or reachable via Tab. @types/react@18.3 has no `inert` attribute
        // typing yet, so this uses the pre-`inert` accessible pattern
        // instead: aria-hidden pulls it out of the accessibility tree, and
        // every focusable child additionally gets tabIndex={-1} while closed
        // (see the tabIndex spread below on the close button / nav rows /
        // sign-out row) so Tab cannot land on an invisible row even in
        // browsers that still expose aria-hidden descendants to sequential
        // focus. The Esc/focus-trap listeners are themselves already gated
        // to `open` (see the effects above), so none of this fires while
        // closed regardless.
        aria-hidden={!open}
      >
        <div className={styles.topRow}>
          <div className={styles.brand}>Resolen</div>
          <button
            ref={closeButtonRef}
            type="button"
            className={styles.closeButton}
            aria-label="Close menu"
            onClick={onClose}
            tabIndex={open ? undefined : -1}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M5 5l14 14M19 5L5 19"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <nav className={styles.nav} aria-label="Primary">
          {SIDE_NAV_ITEMS.map((item) => {
            const active = isSideNavItemActive(item, location.pathname);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={active ? `${styles.navRow} ${styles.active}` : styles.navRow}
                aria-current={active ? 'page' : undefined}
                onClick={onClose}
                tabIndex={open ? undefined : -1}
              >
                <span className={styles.navIcon} aria-hidden="true">
                  {NAV_ICONS[item.to]}
                </span>
                {item.label}
              </Link>
            );
          })}
        </nav>

        <button
          type="button"
          className={styles.signOutRow}
          onClick={handleSignOut}
          tabIndex={open ? undefined : -1}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Sign out
        </button>
      </div>
    </>
  );
}

/** Icons transcribed verbatim from TabBar.tsx's own inline SVGs (same
 *  stroke-based family, 1.7 stroke width, 24x24, currentColor, aria-hidden)
 *  so the drawer's rows read as the SAME icon language as the bottom bar
 *  rather than a second, competing icon set. Settings' gear is transcribed
 *  from Home.tsx's own inline settings-gear SVG (that file's aria-label=
 *  "Settings" Link block) per the brief's "match that hand" instruction. */
const NAV_ICONS: Record<string, JSX.Element> = {
  '/': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M3 11l9-7 9 7M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  '/refine': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M21 12a8 8 0 0 1-11.5 7.2L4 20.5l1.3-5.2A8 8 0 1 1 21 12Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  ),
  '/profiles': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18"
        stroke="currentColor"
        strokeWidth="1.7"
      />
    </svg>
  ),
  '/jobs': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  ),
  '/track': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 19V10M4 19h16M4 19V5M12 19v-6M20 19v-9"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  '/settings': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M10.3 3.2a1.7 1.7 0 0 1 3.4 0l.2 1.1a1.7 1.7 0 0 0 2.4 1.2l1-.5a1.7 1.7 0 0 1 2.1.7l.9 1.6a1.7 1.7 0 0 1-.4 2.2l-.9.7a1.7 1.7 0 0 0 0 2.4l.9.7a1.7 1.7 0 0 1 .4 2.2l-.9 1.6a1.7 1.7 0 0 1-2.1.7l-1-.5a1.7 1.7 0 0 0-2.4 1.2l-.2 1.1a1.7 1.7 0 0 1-3.4 0l-.2-1.1a1.7 1.7 0 0 0-2.4-1.2l-1 .5a1.7 1.7 0 0 1-2.1-.7l-.9-1.6a1.7 1.7 0 0 1 .4-2.2l.9-.7a1.7 1.7 0 0 0 0-2.4l-.9-.7a1.7 1.7 0 0 1-.4-2.2l.9-1.6a1.7 1.7 0 0 1 2.1-.7l1 .5a1.7 1.7 0 0 0 2.4-1.2l.2-1.1Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
};

/** Minimal, dependency-free focus trap: on Tab/Shift+Tab, if focus would
 *  leave the drawer's own focusable set, wrap it back to the other end
 *  instead. Scoped to elements INSIDE `container` only — the drawer's
 *  contents are simple enough (a close button, N nav links, a sign-out
 *  button) that a hand-rolled query is proportionate; no focus-trap
 *  dependency needed (mission law: no new deps). */
function trapFocus(e: KeyboardEvent, container: HTMLElement | null) {
  if (!container) return;
  const focusable = container.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const activeEl = document.activeElement;

  if (e.shiftKey && activeEl === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && activeEl === last) {
    e.preventDefault();
    first.focus();
  }
}
